import type { MigrationGroupRollup, MigrationGroupWithRollup } from '../api/migrationGroups';

// FD-472 — the derivations behind the Groups table's tabs, filter, search and
// export. Pure functions over rollups the endpoint already returned, so they are
// unit-testable and the table component stays presentational.
//
// NOTHING HERE READS `Migrations.Status`. That column is written `"active"` at
// creation and no writer ever updates it (FD-269), so a state derived from it
// would read the same word for every group forever. The lifecycle that actually
// moves is the wave credential — `byBprtStatus` — which is what
// `deriveGroupState` reads, exactly as `GroupStatusBar` does.

/**
 * The state vocabulary a Groups tab can filter on.
 *
 * EXHAUSTIVE AND MUTUALLY EXCLUSIVE, by precedence. That is what makes the tab
 * counts sum to the group total — asserted in the tests — so an operator who
 * adds up the tabs and gets a different number is looking at a defect rather
 * than at a group that quietly belongs to none of them.
 */
export type GroupState = 'expired' | 'waveActive' | 'completed' | 'notActivated' | 'noCampaigns';

export const GROUP_STATE_LABEL: Record<GroupState, string> = {
  // "Wave active", never a bare "Active". On a group it means a live wave
  // credential is outstanding somewhere inside it, which is not what "Active"
  // implies to someone scanning a list.
  waveActive: 'Wave active',
  expired: 'Expired wave',
  completed: 'Completed',
  notActivated: 'Not activated',
  noCampaigns: 'No campaigns',
};

/**
 * Classify a group by the state of its campaigns' wave credentials.
 *
 * Precedence puts `expired` FIRST, ahead of `waveActive`. A group holding both
 * a live wave and a lapsed one is scanned for the lapsed one: an expired
 * credential is a wave that can no longer deliver a flight plan, which is a
 * human's problem now, while a live wave is the system working.
 */
export function deriveGroupState(rollup: MigrationGroupRollup): GroupState {
  const total = rollup.migrationCount ?? 0;
  if (total <= 0) return 'noCampaigns';

  const by = rollup.byBprtStatus ?? {};
  if ((by.expired ?? 0) > 0) return 'expired';
  if ((by.active ?? 0) > 0) return 'waveActive';
  if ((by.revoked ?? 0) === total) return 'completed';
  return 'notActivated';
}

/** How many groups fall in each state. Keys with a zero count are omitted. */
export function countGroupStates(groups: MigrationGroupWithRollup[]): Partial<Record<GroupState, number>> {
  const counts: Partial<Record<GroupState, number>> = {};
  for (const g of groups) {
    const state = deriveGroupState(g.rollup);
    counts[state] = (counts[state] ?? 0) + 1;
  }
  return counts;
}

/**
 * Every destination tenant any group touches, deduped, for the filter.
 *
 * Sorted by domain so the dropdown is stable between renders; a tenant with no
 * resolved domain sorts on its id and is displayed as its id, never as blank.
 */
export function collectDestinations(
  groups: MigrationGroupWithRollup[],
): Array<{ tenantId: string; label: string }> {
  const seen = new Map<string, string>();
  for (const g of groups) {
    for (const d of g.rollup.destinations ?? []) {
      if (!seen.has(d.tenantId)) seen.set(d.tenantId, d.tenantDomain?.trim() || d.tenantId);
    }
  }
  return [...seen.entries()]
    .map(([tenantId, label]) => ({ tenantId, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export interface GroupFilters {
  /** Free text over name AND description. Empty string means no filter. */
  query: string;
  /** `undefined` = the All tab. */
  state?: GroupState;
  /** A destination tenant id, or `undefined` for every tenant. */
  tenantId?: string;
}

/**
 * Apply the toolbar to the loaded set.
 *
 * CLIENT-SIDE, AND COMPLETE. `GET /v1/migration-groups` returns every group in
 * one partition scan (FD-ADR-030 §7), so unlike the devices prefix search there
 * is no page boundary and therefore no "of loaded" caveat the UI has to write.
 * If that endpoint ever paginates, this comment is the thing that has to change
 * first.
 */
export function filterGroups(
  groups: MigrationGroupWithRollup[],
  filters: GroupFilters,
): MigrationGroupWithRollup[] {
  const q = filters.query.trim().toLowerCase();
  return groups.filter((g) => {
    if (filters.state && deriveGroupState(g.rollup) !== filters.state) return false;
    if (filters.tenantId && !(g.rollup.destinations ?? []).some((d) => d.tenantId === filters.tenantId)) {
      return false;
    }
    if (q) {
      const haystack = `${g.name} ${g.description ?? ''}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
}

/**
 * When did anything in this group last change?
 *
 * `rollup.lastUpdatedAt` is the most recent change to any member campaign;
 * `updatedAt` is the last edit to the group RECORD itself. The column is called
 * "Last activity" rather than the board's "Last modified" because the useful
 * fact is the first, and "modified" reads as the second. A group with no
 * campaigns has no member activity, so it falls back to its own row — which is
 * the only honest answer there.
 */
export function groupLastActivity(group: MigrationGroupWithRollup): string | null {
  return group.rollup.lastUpdatedAt ?? group.updatedAt ?? null;
}

/** One CSV field, RFC 4180: quote when it must be quoted, double inner quotes. */
function csvField(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** `{ active: 2, revoked: 1 }` as `active 2 | revoked 1`, for one CSV cell. */
function countsToCell(counts: Record<string, number> | undefined): string {
  return Object.entries(counts ?? {})
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `${k} ${n}`)
    .join(' | ');
}

export const GROUPS_CSV_HEADER = [
  'Group',
  'Description',
  'State',
  'Campaigns',
  'Devices assigned',
  'Wave credentials',
  'Destination tenants',
  'Last activity',
  'Group ID',
] as const;

/**
 * The FILTERED rows as CSV — never the whole set.
 *
 * Exporting everything while the table shows a subset is the export equivalent
 * of a chart that does not match its own caption; the button says "Export" next
 * to the filters, so it exports what the filters produced.
 */
export function groupsToCsv(groups: MigrationGroupWithRollup[]): string {
  const rows = groups.map((g) =>
    [
      g.name,
      g.description ?? '',
      GROUP_STATE_LABEL[deriveGroupState(g.rollup)],
      String(g.rollup.migrationCount ?? 0),
      String(g.rollup.deviceCount ?? 0),
      countsToCell(g.rollup.byBprtStatus),
      (g.rollup.destinations ?? []).map((d) => d.tenantDomain?.trim() || d.tenantId).join(' | '),
      groupLastActivity(g) ?? '',
      g.migrationGroupId,
    ].map(csvField).join(','),
  );
  return [GROUPS_CSV_HEADER.join(','), ...rows].join('\r\n');
}
